import {
  Children,
  type ComponentPropsWithoutRef,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
  type Ref,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { TOAST_HOST_ATTRIBUTE, registerModalSlot } from '../lib/modal-layers.js';
import { cn } from '../lib/utils.js';

/**
 * The behaviour behind `dialog.tsx` and `confirm-dialog.tsx`, built on the native `<dialog>`.
 *
 * PRIVATE to those two files: `index.ts` does not re-export this module, so nothing here is part
 * of the package's contract. The styled, public pieces live in `dialog.tsx`.
 *
 * `showModal()` gives us what a JavaScript focus trap only imitates: the rest of the page goes
 * inert (no Tab, no click, no screen reader wandering behind the dialog), the dialog renders in
 * the top layer above every z-index, and `::backdrop` paints the overlay. What stays here is
 * keeping React in charge of the open state — the browser is never allowed to close the dialog
 * on its own, it only asks, and `onOpenChange(false)` is the answer.
 */

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/* -------------------------------------------------------------------------------------------------
 * Slot: `asChild` for the trigger, close, title and description.
 * -----------------------------------------------------------------------------------------------*/

type AnyProps = Record<string, unknown>;

function setRef<T>(ref: Ref<T> | undefined, value: T | null): void {
  if (typeof ref === 'function') ref(value);
  else if (ref) (ref as MutableRefObject<T | null>).current = value;
}

function composeRefs<T>(...refs: (Ref<T> | undefined)[]): (node: T | null) => void {
  return (node) => {
    for (const ref of refs) setRef(ref, node);
  };
}

/**
 * Renders its only child with the slot's props merged in: handlers run child first, then the
 * slot's own; class names are joined; styles merge with the child's winning; refs are composed.
 */
const Slot = forwardRef<HTMLElement, AnyProps & { children?: ReactNode }>(function Slot(
  { children, ...slotProps },
  forwardedRef,
) {
  const child = Children.only(children);
  if (!isValidElement(child)) return null;

  const childProps = child.props as AnyProps;
  const merged: AnyProps = { ...slotProps, ...childProps };
  for (const key of Object.keys(slotProps)) {
    const slotValue = slotProps[key];
    const childValue = childProps[key];
    if (/^on[A-Z]/.test(key) && typeof slotValue === 'function') {
      merged[key] =
        typeof childValue === 'function'
          ? (...args: unknown[]) => {
              (childValue as (...a: unknown[]) => void)(...args);
              (slotValue as (...a: unknown[]) => void)(...args);
            }
          : slotValue;
    } else if (key === 'className') {
      merged[key] = cn(slotValue as string, childValue as string);
    } else if (key === 'style') {
      merged[key] = { ...(slotValue as object), ...(childValue as object) };
    }
  }
  // React 18 keeps an element's ref beside its props, not inside them.
  const childRef = (child as unknown as { ref?: Ref<HTMLElement> }).ref;
  merged.ref = composeRefs(forwardedRef, childRef);
  return cloneElement(child, merged);
});

/** Runs `theirs`, then `ours` unless `theirs` called `preventDefault()`. */
function composeHandlers<E extends { defaultPrevented: boolean }>(
  theirs: ((event: E) => void) | undefined,
  ours: (event: E) => void,
): (event: E) => void {
  return (event) => {
    theirs?.(event);
    if (!event.defaultPrevented) ours(event);
  };
}

/* -------------------------------------------------------------------------------------------------
 * Root
 * -----------------------------------------------------------------------------------------------*/

interface DialogRootContextValue {
  open: boolean;
  modal: boolean;
  contentId: string;
  triggerRef: MutableRefObject<HTMLElement | null>;
  setOpen: (open: boolean) => void;
}

const DialogRootContext = createContext<DialogRootContextValue | null>(null);

function useDialogRoot(component: string): DialogRootContextValue {
  const context = useContext(DialogRootContext);
  if (!context) throw new Error(`<${component}> must be used inside <Dialog>`);
  return context;
}

export interface DialogRootProps {
  children?: ReactNode;
  /** Controlled open state. When set, the dialog never closes itself — it asks `onOpenChange`. */
  open?: boolean;
  /** The initial state when uncontrolled. */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * `true` (the default) opens with `showModal()`: inert page, backdrop, scroll lock. `false`
   * opens with `show()`, leaving the page usable behind it.
   */
  modal?: boolean;
}

export function DialogRoot({
  children,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  modal = true,
}: DialogRootProps): ReactElement {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : uncontrolledOpen;

  // Read through refs so `setOpen` keeps one identity: the content subscribes to native events
  // once per opening, and a fresh callback every render would not reach those listeners anyway.
  const latest = useRef({ open, controlled, onOpenChange });
  latest.current = { open, controlled, onOpenChange };

  const setOpen = useCallback((next: boolean) => {
    const { open: current, controlled: isControlled, onOpenChange: notify } = latest.current;
    if (!isControlled) setUncontrolledOpen(next);
    if (next !== current) notify?.(next);
  }, []);

  const contentId = useId();
  const triggerRef = useRef<HTMLElement | null>(null);

  const value = useMemo<DialogRootContextValue>(
    () => ({ open, modal, contentId, triggerRef, setOpen }),
    [open, modal, contentId, setOpen],
  );

  return <DialogRootContext.Provider value={value}>{children}</DialogRootContext.Provider>;
}

/* -------------------------------------------------------------------------------------------------
 * Trigger and Close
 * -----------------------------------------------------------------------------------------------*/

export type DialogButtonProps = ComponentPropsWithoutRef<'button'> & {
  /** Render the child element (a `<Button>`, a link) instead of a `<button>`. */
  asChild?: boolean;
};

export const DialogTriggerPrimitive = forwardRef<HTMLButtonElement, DialogButtonProps>(
  function DialogTrigger({ asChild = false, onClick, ...props }, forwardedRef) {
    const context = useDialogRoot('DialogTrigger');
    const Component = asChild ? Slot : 'button';
    return (
      <Component
        type="button"
        aria-haspopup="dialog"
        aria-expanded={context.open}
        aria-controls={context.open ? context.contentId : undefined}
        data-state={context.open ? 'open' : 'closed'}
        {...props}
        ref={composeRefs(forwardedRef, context.triggerRef as Ref<HTMLButtonElement>)}
        onClick={composeHandlers(onClick, () => {
          context.setOpen(!context.open);
        })}
      />
    );
  },
);

export const DialogClosePrimitive = forwardRef<HTMLButtonElement, DialogButtonProps>(
  function DialogClose({ asChild = false, onClick, ...props }, forwardedRef) {
    const context = useDialogRoot('DialogClose');
    const Component = asChild ? Slot : 'button';
    return (
      <Component
        type="button"
        {...props}
        ref={forwardedRef}
        onClick={composeHandlers(onClick, () => {
          context.setOpen(false);
        })}
      />
    );
  },
);

/* -------------------------------------------------------------------------------------------------
 * Portal
 * -----------------------------------------------------------------------------------------------*/

const PortalContainerContext = createContext<Element | DocumentFragment | null>(null);

export interface DialogPortalProps {
  children?: ReactNode;
  /** Where the dialog is rendered in the DOM. Defaults to `document.body`. */
  container?: Element | DocumentFragment | null;
  /** Accepted for compatibility; the content decides for itself when it is mounted. */
  forceMount?: true;
}

/**
 * Chooses the DOM parent the content portals into. Without one, `document.body` — which keeps a
 * dialog's buttons from belonging to a `<form>` the dialog happens to be declared inside, and
 * keeps sibling dialogs (a confirm over a form) independent in the DOM as well as in the top layer.
 */
export function DialogPortalPrimitive({ children, container }: DialogPortalProps): ReactElement {
  return (
    <PortalContainerContext.Provider value={container ?? null}>
      {children}
    </PortalContainerContext.Provider>
  );
}

/* -------------------------------------------------------------------------------------------------
 * Body scroll lock — shared by every open dialog, so nested ones count rather than fight.
 * -----------------------------------------------------------------------------------------------*/

let scrollLocks = 0;
let savedBodyStyle: { overflow: string; paddingRight: string } | null = null;

function lockBodyScroll(): () => void {
  const body = document.body;
  if (scrollLocks === 0) {
    savedBodyStyle = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
    // Hiding the scrollbar widens the page; pad by its width so nothing behind the backdrop jumps.
    const viewport = document.documentElement.clientWidth;
    const scrollbar = viewport > 0 ? window.innerWidth - viewport : 0;
    if (scrollbar > 0) {
      const padding = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${padding + scrollbar}px`;
    }
    body.style.overflow = 'hidden';
  }
  scrollLocks += 1;

  let released = false;
  return () => {
    if (released) return;
    released = true;
    scrollLocks -= 1;
    if (scrollLocks === 0 && savedBodyStyle) {
      body.style.overflow = savedBodyStyle.overflow;
      body.style.paddingRight = savedBodyStyle.paddingRight;
      savedBodyStyle = null;
    }
  };
}

/* -------------------------------------------------------------------------------------------------
 * The page behind, hidden from assistive technology.
 * -----------------------------------------------------------------------------------------------*/

const hiddenBy = new WeakMap<Element, number>();

/**
 * `aria-hidden` on everything beside the dialog, up to `<body>` — what Radix did. A modal
 * `<dialog>` already takes the page out of the accessibility tree by making it inert; this
 * repeats it for screen readers that predate that, and for tooling with no notion of `inert`
 * (Testing Library's role queries). Counted, so a nested dialog closing leaves the outer one's
 * hiding in place, and an `aria-hidden` the page set itself is never touched.
 *
 * The toast viewport's host is left alone: it is about to move INTO this dialog (see
 * `modal-layers.ts`), and it would carry the attribute with it.
 */
function hideOthers(keep: Element): () => void {
  const hidden: Element[] = [];
  for (let node = keep; node.parentElement && node !== document.body;) {
    const parent: Element = node.parentElement;
    for (const sibling of Array.from(parent.children)) {
      if (sibling === node || sibling.tagName === 'SCRIPT' || sibling.tagName === 'STYLE') continue;
      if (sibling.hasAttribute(TOAST_HOST_ATTRIBUTE)) continue;
      const count = hiddenBy.get(sibling) ?? 0;
      if (count === 0 && sibling.hasAttribute('aria-hidden')) continue;
      hiddenBy.set(sibling, count + 1);
      if (count === 0) sibling.setAttribute('aria-hidden', 'true');
      hidden.push(sibling);
    }
    node = parent;
  }
  return () => {
    for (const sibling of hidden) {
      const count = (hiddenBy.get(sibling) ?? 1) - 1;
      hiddenBy.set(sibling, count);
      if (count === 0) sibling.removeAttribute('aria-hidden');
    }
  };
}

/* -------------------------------------------------------------------------------------------------
 * Focus
 * -----------------------------------------------------------------------------------------------*/

const TABBABLE = [
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]',
  '[contenteditable=""]',
  '[contenteditable="true"]',
].join(',');

function isHidden(element: Element, container: Element): boolean {
  if (window.getComputedStyle(element).visibility === 'hidden') return true;
  for (let node: Element | null = element; node && node !== container; node = node.parentElement) {
    if (node.hasAttribute('hidden') || window.getComputedStyle(node).display === 'none') {
      return true;
    }
  }
  return false;
}

/**
 * Focus the first tabbable control, or the dialog itself when there is none. Links are skipped,
 * as Radix skipped them: landing on "Terms" rather than the first field helps nobody.
 */
function focusFirst(container: HTMLElement): void {
  for (const element of container.querySelectorAll<HTMLElement>(TABBABLE)) {
    if (element.tabIndex < 0 || isHidden(element, container)) continue;
    element.focus();
    if (document.activeElement === element) {
      // Like Radix: a prefilled field opens selected, so typing replaces rather than appends.
      if (element instanceof HTMLInputElement) element.select();
      return;
    }
  }
  container.focus();
}

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

/**
 * The dim layer behind a modal: the <dialog>'s own `::backdrop`. The colour carries a fallback,
 * because before Safari 17.4 and Chrome 122 `::backdrop` inherited nothing from the page, and
 * `var(--foreground)` alone would leave older phones with no dimming at all. A printed receipt
 * must not come out grey, so print drops it.
 */
export const DIALOG_BACKDROP =
  'backdrop:bg-[hsl(var(--foreground,213_30%_16%)/0.4)] print:backdrop:bg-transparent';

/** The toast viewport (`toast.tsx`): presses inside it never count as outside a dialog. */
const TOAST_VIEWPORT = '[role="region"][aria-label^="Notifications"]';

interface DialogContentContextValue {
  defaultTitleId: string;
  defaultDescriptionId: string;
  setTitleId: (id: string | null) => void;
  setDescriptionId: (id: string | null) => void;
}

const DialogContentContext = createContext<DialogContentContextValue | null>(null);

export type PointerDownOutsideEvent = CustomEvent<{ originalEvent: PointerEvent }>;
export type FocusOutsideEvent = CustomEvent<{ originalEvent: FocusEvent }>;

export interface DialogContentPrimitiveProps extends Omit<
  ComponentPropsWithoutRef<'dialog'>,
  'open'
> {
  /** Keep the element mounted while closed (it is hidden, and not shown modally). */
  forceMount?: true;
  /** Fired before focus moves into the dialog on open. `preventDefault()` to place it yourself. */
  onOpenAutoFocus?: (event: Event) => void;
  /** Fired before focus returns to the trigger on close. `preventDefault()` to place it yourself. */
  onCloseAutoFocus?: (event: Event) => void;
  /** Escape was pressed. `preventDefault()` keeps the dialog open. */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  /** A press landed on the backdrop. `preventDefault()` keeps the dialog open. */
  onPointerDownOutside?: (event: PointerDownOutsideEvent) => void;
  /** Focus left a non-modal dialog. (A modal one cannot lose focus: the page behind is inert.) */
  onFocusOutside?: (event: FocusOutsideEvent) => void;
  /** Either of the two above. `preventDefault()` keeps the dialog open. */
  onInteractOutside?: (event: PointerDownOutsideEvent | FocusOutsideEvent) => void;
}

/**
 * The `<dialog>` element. Mounted while open and for as long as its exit animation runs; the
 * children are dropped when it closes, so a form inside starts fresh on the next opening.
 */
export const DialogContentPrimitive = forwardRef<HTMLDialogElement, DialogContentPrimitiveProps>(
  function DialogContentPrimitive({ forceMount, ...props }, forwardedRef) {
    const context = useDialogRoot('DialogContent');
    const container = useContext(PortalContainerContext);
    const [present, setPresent] = useState(context.open);
    // Opening never waits: a render-phase update, so the very render that opens also mounts.
    if (context.open && !present) setPresent(true);

    const onExitComplete = useCallback(() => {
      setPresent(false);
    }, []);

    const active = context.open || present;
    if ((!active && !forceMount) || typeof document === 'undefined') return null;

    return createPortal(
      <DialogContentImpl
        {...props}
        ref={forwardedRef}
        active={active}
        forceMount={forceMount === true}
        onExitComplete={onExitComplete}
      />,
      container ?? document.body,
    );
  },
);

interface DialogContentImplProps extends Omit<DialogContentPrimitiveProps, 'forceMount'> {
  /** Shown natively: open, or still animating out. */
  active: boolean;
  forceMount: boolean;
  onExitComplete: () => void;
}

const DialogContentImpl = forwardRef<HTMLDialogElement, DialogContentImplProps>(
  function DialogContentImpl(
    {
      active,
      forceMount,
      onExitComplete,
      onOpenAutoFocus,
      onCloseAutoFocus,
      onEscapeKeyDown,
      onPointerDownOutside,
      onFocusOutside,
      onInteractOutside,
      children,
      style,
      ...props
    },
    forwardedRef,
  ) {
    const context = useDialogRoot('DialogContent');
    const { open, modal, setOpen, triggerRef, contentId } = context;
    const nodeRef = useRef<HTMLDialogElement | null>(null);
    const slotRef = useRef<HTMLDivElement | null>(null);

    // Children mount one commit AFTER the dialog is shown — the same two-step Radix's portal
    // took. A child's `autoFocus` focuses during its own commit, and a field inside a dialog
    // that is not open yet is not rendered, so cannot take focus.
    const [ready, setReady] = useState(forceMount);

    const defaultTitleId = useId();
    const defaultDescriptionId = useId();
    const [titleId, setTitleId] = useState<string | null>(null);
    const [descriptionId, setDescriptionId] = useState<string | null>(null);
    // A title unmounting WITH the dialog has nothing to unregister from; only one that goes
    // while the dialog stays should clear its id. (This cleanup runs before the children's.)
    const unmounting = useRef(false);
    useIsomorphicLayoutEffect(() => {
      unmounting.current = false;
      return () => {
        unmounting.current = true;
      };
    }, []);
    const contentContext = useMemo<DialogContentContextValue>(
      () => ({
        defaultTitleId,
        defaultDescriptionId,
        setTitleId: (id) => {
          if (!unmounting.current) setTitleId(id);
        },
        setDescriptionId: (id) => {
          if (!unmounting.current) setDescriptionId(id);
        },
      }),
      [defaultTitleId, defaultDescriptionId],
    );

    // The handlers change every render; the native listeners read the latest through this ref.
    const handlers = useRef({
      onOpenAutoFocus,
      onCloseAutoFocus,
      onEscapeKeyDown,
      onPointerDownOutside,
      onFocusOutside,
      onInteractOutside,
    });
    handlers.current = {
      onOpenAutoFocus,
      onCloseAutoFocus,
      onEscapeKeyDown,
      onPointerDownOutside,
      onFocusOutside,
      onInteractOutside,
    };
    const openRef = useRef(open);
    openRef.current = open;

    /** Where focus was inside the dialog when React's StrictMode unmounted it for a moment. */
    const focusMemory = useRef<HTMLElement | null>(null);
    /** Whether `close()` is ours, so the `close` event it fires is not mistaken for the browser's. */
    const closingRef = useRef(false);

    // Show the dialog natively — and close it, lock and unlock scroll, return focus — in step
    // with `active`. Everything a dialog does to the page outside it happens here and is undone here.
    useIsomorphicLayoutEffect(() => {
      const node = nodeRef.current;
      if (!active || !node) return;

      const previouslyFocused =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;

      if (!node.open) {
        if (modal && typeof node.showModal === 'function') node.showModal();
        else if (!modal && typeof node.show === 'function') node.show();
        // Without native <dialog> support (jsdom, very old Safari) it still shows and dismisses;
        // it just has no top layer.
        else node.setAttribute('open', '');
      }
      const unlockScroll = modal ? lockBodyScroll() : () => undefined;
      const showOthers = modal ? hideOthers(node) : () => undefined;
      setReady(true);

      return () => {
        if (node.contains(document.activeElement) && document.activeElement !== node) {
          focusMemory.current = document.activeElement as HTMLElement;
        }
        closingRef.current = true;
        if (node.open) {
          if (typeof node.close === 'function') node.close();
          else node.removeAttribute('open');
        }
        closingRef.current = false;
        unlockScroll();
        showOthers();

        const event = new Event('dialog.closeAutoFocus', { cancelable: true });
        handlers.current.onCloseAutoFocus?.(event);
        if (event.defaultPrevented) return;
        const target =
          previouslyFocused?.isConnected && previouslyFocused !== document.body
            ? previouslyFocused
            : triggerRef.current;
        target?.focus();
      };
    }, [active, modal, triggerRef]);

    // While a modal is OPEN, anything that has to stay usable above it — the toast viewport — lives
    // in the slot at the end of its box (see `modal-layers.ts`). Open, not merely mounted: a toast
    // raised as the dialog closes belongs to the page it is returning to, not to a box on its way
    // out. Declared after the effect above, so the dialog is already showing when the slot is taken.
    useIsomorphicLayoutEffect(() => {
      const slot = slotRef.current;
      if (!open || !modal || !slot) return;
      return registerModalSlot(slot);
    }, [open, modal]);

    // Once the children are in: move focus inside, unless a child's autoFocus already did.
    useIsomorphicLayoutEffect(() => {
      const node = nodeRef.current;
      if (!active || !ready || !node) return;

      const remembered = focusMemory.current;
      focusMemory.current = null;
      if (remembered?.isConnected && node.contains(remembered)) {
        remembered.focus();
        return;
      }
      const focused = document.activeElement;
      if (focused && focused !== node && node.contains(focused)) return;

      const event = new Event('dialog.openAutoFocus', { cancelable: true });
      handlers.current.onOpenAutoFocus?.(event);
      if (!event.defaultPrevented) focusFirst(node);
    }, [active, ready]);

    // Closing: keep the dialog up until its exit animation has played, then let it unmount.
    const enterAnimation = useRef('');
    useIsomorphicLayoutEffect(() => {
      const node = nodeRef.current;
      if (!node) return;
      const animationName = window.getComputedStyle(node).animationName;
      if (open) {
        enterAnimation.current = animationName;
        return;
      }
      if (!active) return;

      // Wait only for an animation the closed state STARTED. If none did, or the dialog is not
      // being rendered, there is nothing to watch, and waiting would leave an invisible modal
      // blocking the page.
      const animates =
        node.open &&
        Boolean(animationName) &&
        animationName !== 'none' &&
        animationName !== enterAnimation.current;
      if (!animates) {
        onExitComplete();
        return;
      }
      const finish = (event: AnimationEvent): void => {
        // The backdrop's animation ends on this element too, flagged with its pseudo-element.
        if (event.target === node && !event.pseudoElement) onExitComplete();
      };
      node.addEventListener('animationend', finish);
      node.addEventListener('animationcancel', finish);
      // Belt and braces: an animation that never reports back must not strand a modal.
      const fallback = window.setTimeout(onExitComplete, 1000);
      return () => {
        node.removeEventListener('animationend', finish);
        node.removeEventListener('animationcancel', finish);
        window.clearTimeout(fallback);
      };
    }, [open, active, onExitComplete]);

    // Escape, the backdrop, and the browser's own close requests. Native listeners on the
    // element rather than React props: a React event bubbles along the component tree, so a
    // nested dialog's Escape would otherwise reach (and close) the dialog it was opened from.
    useIsomorphicLayoutEffect(() => {
      const node = nodeRef.current;
      if (!active || !node) return;

      const dismissWithEscape = (event: KeyboardEvent): void => {
        handlers.current.onEscapeKeyDown?.(event);
        const kept = event.defaultPrevented;
        if (!kept && openRef.current) setOpen(false);
      };

      const onKeyDown = (event: KeyboardEvent): void => {
        if (event.key !== 'Escape' || event.isComposing) return;
        // A control inside (a popover, a combobox) already used this Escape for itself.
        if (event.defaultPrevented) return;
        dismissWithEscape(event);
        // Never let the browser act on it: closing is React's decision, made just above.
        event.preventDefault();
      };

      // A close request that did not come from a key we saw — the Android back gesture, or an
      // Escape pressed while focus was on <body>. The browser wants to close; we ask instead.
      const onCancel = (event: Event): void => {
        event.preventDefault();
        dismissWithEscape(new KeyboardEvent('keydown', { key: 'Escape', cancelable: true }));
      };

      // The browser closed the dialog anyway (a form with method="dialog", an uncancellable
      // close request). Put it back, and let the open state decide.
      const onClose = (): void => {
        // `close` is queued, so one can arrive after StrictMode has already shown the dialog
        // again; a dialog that is open is not closed, whatever a stale event says.
        if (closingRef.current || node.open || !node.isConnected) return;
        if (modal && typeof node.showModal === 'function') node.showModal();
        else node.setAttribute('open', '');
        if (openRef.current) setOpen(false);
      };

      // A press on the backdrop. The backdrop is not an element: a press on it lands on the
      // <dialog> itself, outside the box the dialog draws. A press that starts inside — selecting
      // text and dragging out — is not a dismissal, so both ends must be outside.
      let pressStartedOutside = false;
      const isOutside = (event: MouseEvent): boolean => {
        if (event.target !== node) return false;
        const box = node.getBoundingClientRect();
        return (
          event.clientX < box.left ||
          event.clientX > box.right ||
          event.clientY < box.top ||
          event.clientY > box.bottom
        );
      };
      const onPressStart = (event: Event): void => {
        pressStartedOutside = false;
        if (!isOutside(event as MouseEvent)) return;
        const outside: PointerDownOutsideEvent = new CustomEvent('dialog.pointerDownOutside', {
          cancelable: true,
          detail: { originalEvent: event as PointerEvent },
        });
        handlers.current.onPointerDownOutside?.(outside);
        handlers.current.onInteractOutside?.(outside);
        pressStartedOutside = !outside.defaultPrevented;
      };
      const onClick = (event: MouseEvent): void => {
        const dismiss = pressStartedOutside && isOutside(event);
        pressStartedOutside = false;
        if (dismiss && openRef.current) setOpen(false);
      };
      // The dialog is focusable (tabindex -1), so a press on its backdrop would pull focus off
      // the control that had it — Cancel, in a confirm that stays open. A backdrop press moves
      // no focus.
      const onMouseDown = (event: MouseEvent): void => {
        if (isOutside(event)) event.preventDefault();
      };
      const pressStart = 'PointerEvent' in window ? 'pointerdown' : 'mousedown';

      node.addEventListener('keydown', onKeyDown);
      node.addEventListener('cancel', onCancel);
      node.addEventListener('close', onClose);
      node.addEventListener(pressStart, onPressStart);
      node.addEventListener('mousedown', onMouseDown);
      node.addEventListener('click', onClick);

      // A non-modal dialog leaves the page live, so a press or focus outside it is a dismissal.
      const onDocumentPress = (event: Event): void => {
        if (node.contains(event.target as Node)) return;
        // Pressing a toast is not leaving the dialog (Radix treated the toast viewport the same).
        if ((event.target as Element | null)?.closest?.(TOAST_VIEWPORT)) return;
        const outside: PointerDownOutsideEvent = new CustomEvent('dialog.pointerDownOutside', {
          cancelable: true,
          detail: { originalEvent: event as PointerEvent },
        });
        handlers.current.onPointerDownOutside?.(outside);
        handlers.current.onInteractOutside?.(outside);
        if (!outside.defaultPrevented && openRef.current) setOpen(false);
      };
      const onDocumentFocus = (event: FocusEvent): void => {
        if (node.contains(event.target as Node)) return;
        const outside: FocusOutsideEvent = new CustomEvent('dialog.focusOutside', {
          cancelable: true,
          detail: { originalEvent: event },
        });
        handlers.current.onFocusOutside?.(outside);
        handlers.current.onInteractOutside?.(outside);
        if (!outside.defaultPrevented && openRef.current) setOpen(false);
      };
      if (!modal) {
        document.addEventListener(pressStart, onDocumentPress);
        document.addEventListener('focusin', onDocumentFocus);
      }

      return () => {
        node.removeEventListener('keydown', onKeyDown);
        node.removeEventListener('cancel', onCancel);
        node.removeEventListener('close', onClose);
        node.removeEventListener(pressStart, onPressStart);
        node.removeEventListener('mousedown', onMouseDown);
        node.removeEventListener('click', onClick);
        document.removeEventListener(pressStart, onDocumentPress);
        document.removeEventListener('focusin', onDocumentFocus);
      };
    }, [active, modal, setOpen]);

    return (
      <dialog
        id={contentId}
        tabIndex={-1}
        aria-labelledby={titleId ?? undefined}
        aria-describedby={descriptionId ?? undefined}
        aria-modal={modal ? true : undefined}
        data-state={open ? 'open' : 'closed'}
        {...props}
        // A force-mounted dialog that is closed must not show: author `display` rules (`flex`)
        // beat the browser's own `dialog:not([open]) { display: none }`.
        style={active ? style : { ...style, display: 'none' }}
        ref={composeRefs(forwardedRef, nodeRef)}
      >
        <DialogContentContext.Provider value={contentContext}>
          {ready ? children : null}
        </DialogContentContext.Provider>
        {/* Last, so what moves in comes after the dialog's own controls in the Tab order. No box of
            its own: a flex `gap` would otherwise open an empty row at the bottom of the dialog. */}
        <div ref={slotRef} data-modal-slot="" style={{ display: 'contents' }} />
      </dialog>
    );
  },
);

/* -------------------------------------------------------------------------------------------------
 * Title and Description — wired to the dialog through aria-labelledby / aria-describedby.
 * -----------------------------------------------------------------------------------------------*/

type LabelPart = 'title' | 'description';

function useLabelId(part: LabelPart, idProp: string | undefined): string | undefined {
  const content = useContext(DialogContentContext);
  const id = idProp ?? (part === 'title' ? content?.defaultTitleId : content?.defaultDescriptionId);
  useIsomorphicLayoutEffect(() => {
    if (!content || !id) return;
    const register = part === 'title' ? content.setTitleId : content.setDescriptionId;
    register(id);
    return () => {
      register(null);
    };
  }, [content, id, part]);
  return id;
}

export const DialogTitlePrimitive = forwardRef<
  HTMLHeadingElement,
  ComponentPropsWithoutRef<'h2'> & { asChild?: boolean }
>(function DialogTitle({ asChild = false, id, ...props }, ref) {
  const labelId = useLabelId('title', id);
  const Component = asChild ? Slot : 'h2';
  return <Component {...props} id={labelId} ref={ref} />;
});

export const DialogDescriptionPrimitive = forwardRef<
  HTMLParagraphElement,
  ComponentPropsWithoutRef<'p'> & { asChild?: boolean }
>(function DialogDescription({ asChild = false, id, ...props }, ref) {
  const labelId = useLabelId('description', id);
  const Component = asChild ? Slot : 'p';
  return <Component {...props} id={labelId} ref={ref} />;
});

/** The root's open state, for the overlay (which has no element of its own to sync). */
export function useDialogOpen(): boolean {
  return useDialogRoot('DialogOverlay').open;
}
